import { useCallback, useRef, useSyncExternalStore } from 'react'

function shallowEqual(a, b) {
  if (Object.is(a, b)) {
    return true
  }

  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) {
    return false
  }

  const keysA = Object.keys(a)
  const keysB = Object.keys(b)

  return keysA.length === keysB.length
    && keysA.every((key) => Object.prototype.hasOwnProperty.call(b, key) && Object.is(a[key], b[key]))
}

// `window.pageConfig` is replaced wholesale by each config script (`/products/:slug.js` and
// `/load-services.js` both spread into a new object, then dispatch the event), so the object
// identity is the store version and a changed selection is the only thing that re-renders.
//
// Pass `select` to subscribe to a slice: `usePageConfig({ select: (config) => config?.product?.id })`.
// The selector must be stable (module-level) and may return an object, which is compared
// shallowly so a selection whose fields did not change keeps its previous reference.
function usePageConfig({ listener = 'window-config-loaded', checkIfAvailable = true, select } = {}) {
  const landedRef = useRef(false)
  const selectionRef = useRef(null)

  // The server has no config, and hydration renders with this same value; seeding the cache
  // with it keeps the client snapshot reference-equal while the config is still absent, so
  // hydration does not schedule a re-render just because an object selector returned a copy.
  if (selectionRef.current === null) {
    selectionRef.current = { config: undefined, select, selection: select ? select(undefined) : undefined }
  }

  const getServerSnapshot = () => selectionRef.current.selection

  const subscribe = useCallback((onStoreChange) => {
    const handleConfigLoaded = () => {
      landedRef.current = true
      onStoreChange()
    }

    window.addEventListener(listener, handleConfigLoaded)

    return () => {
      window.removeEventListener(listener, handleConfigLoaded)
    }
  }, [listener])

  const getSnapshot = () => {
    const config = window.pageConfig
    // /load-services.js dispatches its config events exactly once, then sets
    // `servicesLoaded`. A component whose effects run after that (heavy hydration
    // losing the race to the `load`-triggered fetch) would never receive the event
    // and stay on the SSR default forever, so adopt the already-landed config.
    const available = checkIfAvailable || landedRef.current || config?.servicesLoaded === true

    if (!select) {
      return available ? config : undefined
    }

    const visibleConfig = available ? config : undefined
    const cached = selectionRef.current

    if (cached.config === visibleConfig && cached.select === select) {
      return cached.selection
    }

    const selection = select(visibleConfig)
    const stableSelection = shallowEqual(cached.selection, selection) ? cached.selection : selection

    selectionRef.current = { config: visibleConfig, select, selection: stableSelection }

    return stableSelection
  }

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

export default usePageConfig
